import type { ThemeTokens } from '../theme'
import { DEFAULT_USER_BUDGET, UNLIMITED_BUDGET } from '../constants'

export interface BurnBarProps {
  spend: number
  cap: number | null | undefined
  tokens: ThemeTokens
}

/**
 * Spend-vs-cap meter, ported from `scripts/chugpt-admin.py`'s `_burn_bar`:
 * a filled track under the cap, empty above it, colour-staged as spend
 * approaches the ceiling. Deliberately reuses only existing brand tokens
 * (`brand` green under 80%, `ring` teal 80-99%, `danger` red at/over the
 * cap) rather than inventing a new "warning" hue outside the palette in
 * `theme.ts`.
 */
export function BurnBar({ spend, cap, tokens }: BurnBarProps) {
  // Only the explicit sentinel is truly unlimited. A null cap means no
  // per-user override, so the global default ($100) is the effective ceiling
  // the gateway enforces — burn is measured against that, not treated as ∞
  // (which is what made a null-cap row read as both "default $100" and ∞).
  const unlimited = cap != null && cap >= UNLIMITED_BUDGET
  const effectiveCap = cap == null ? DEFAULT_USER_BUDGET : cap
  const safeCap = !unlimited && effectiveCap > 0 ? effectiveCap : 0
  const frac = unlimited ? 0 : safeCap > 0 ? Math.min(spend / safeCap, 1) : 1
  const pctLabel = unlimited ? 'unlimited' : `${Math.round(frac * 100)}%`
  const barColor = unlimited ? tokens.border : frac >= 1 ? tokens.danger : frac >= 0.8 ? tokens.ring : tokens.brand
  const capText = unlimited ? 'unlimited' : `$${effectiveCap.toFixed(0)}`
  const label = `Spend $${spend.toFixed(2)} of ${capText} cap (${pctLabel})`

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: '0.55rem', minWidth: '10rem' }}>
      <div
        role="img"
        aria-label={label}
        title={label}
        style={{
          flex: 1,
          height: '0.5rem',
          borderRadius: '9999px',
          background: tokens.surfaceRaised,
          border: `1px solid ${tokens.border}`,
          overflow: 'hidden',
        }}
      >
        <div
          style={{
            width: `${unlimited ? 100 : frac * 100}%`,
            height: '100%',
            background: barColor,
            opacity: unlimited ? 0.5 : 1,
          }}
        />
      </div>
      <span style={{ fontSize: '0.76rem', color: tokens.textSecondary, minWidth: '2.6rem', textAlign: 'right' }}>
        {unlimited ? '∞' : pctLabel}
      </span>
    </div>
  )
}
